import { memoize } from 'lodash-es';
import { ReactNode } from 'react';

import { formatBytes, formatDuration } from 'core/services/utils';

import {
  DateRenderer,
  ParsedDateRenderer,
  ParsedTimeRenderer,
  RecordColumnMapType,
  RecordType,
} from '../shared';
import {
  primaryRenderers as priRenderer,
  secondaryRenderers as secRenderer,
} from './legacyInfoRenderers';
import {
  BooleanRenderer,
  CertSubjectRenderer,
  DataChannelRenderer,
  DnsAnswerRenderer,
  HashRenderer,
  HeadersRenderer,
  HttpRequestRenderer,
  HttpResponseRenderer,
  MetadataRenderer,
  PendingCommandsRenderer,
  SanitizedRenderer,
  SipHeadersRenderer,
  SipRequestRenderer,
  StoredFilePathRenderer,
  UppercaseRenderer,
} from './recordHelpers';

interface RecordNameMapType {
  [name: string]: string;
}

export const recordNameMap: RecordNameMapType = {
  canary: 'Canary',
  dcerpc: 'DCE/RPC',
  dhcp: 'DHCP',
  dns: 'DNS',
  auth: 'Authentication',
  email: 'Email',
  endpoint: 'Endpoint',
  file: 'File',
  flow: 'Flow',
  ftp: 'FTP',
  http: 'HTTP',
  irc: 'IRC',
  kerberos: 'Kerberos',
  notice: 'Notice',
  ntlm: 'NTLM',
  proxy: 'Proxy',
  radius: 'RADIUS',
  rdp: 'RDP',
  rfb: 'RFB',
  sip: 'SIP',
  smb: 'SMB',
  smtp: 'SMTP',
  snmp: 'SNMP',
  socks: 'SOCKS',
  ssh: 'SSH',
  threat: 'Threat',
  tls: 'TLS',
  vuln: 'Vulnerability',
  windows: 'Windows',
};

interface RecordPropRenderMapType {
  [property: string]: {
    label: string;
    renderer?: (value?: any, record?: RecordType) => ReactNode;
  };
}

export interface RecordRenderMapType {
  [name: string]: RecordPropRenderMapType;
}

const commonRecordMeta: RecordPropRenderMapType = {
  'common.event_time': { label: 'Event Time', renderer: DateRenderer },
  'common.meta_time_receipt': {
    label: 'Meta Time Receipt',
    renderer: DateRenderer,
  },
  'common.meta_time_persist': {
    label: 'Meta Time Persist',
    renderer: DateRenderer,
  },
};

const recordRenderMap: RecordRenderMapType = {
  auth: {
    'log_meta.device_event_id': { label: 'Event Id' },
    'log_meta.device_vendor': { label: 'Vendor' },
    'log_meta.device_product': { label: 'Product' },
    'log_meta.log_type': { label: 'Log Type' },
    auth_domain: { label: 'Authentication Domain' },
    auth_method: { label: 'Authentication Method' },
    endpoint_username: { label: 'Endpoint Username' },
    endpoint: { label: 'Endpoint' },
    service: { label: 'Service' },
    success: { label: 'Authentication Successful', renderer: BooleanRenderer },
    user_realm: { label: 'User Realm' },
    user_role: { label: 'User Role' },
  },
  canary: {
    hatchtime: { label: 'Hatch Time' },
    debug: { label: 'Debug' },
  },
  dcerpc: {
    'dst_ip.address': { label: 'Dst. IP' },
    'src_ip.address': { label: 'Src. IP' },
    dst_port: { label: 'Dst. Port' },
    endpoint: { label: 'Endpoint' },
    named_pipe: { label: 'Named Pipe' },
    operation: { label: 'Operation' },
    rtt: { label: 'RTT' },
    src_port: { label: 'Src. Port' },
  },
  dhcp: {
    'assigned_ip.address': { label: 'Assigned IP' },
    'dst_ip.address': { label: 'Dst. IP' },
    'src_ip.address': { label: 'Src. IP' },
    dst_port: { label: 'Dst. Port' },
    lease_time: {
      label: 'Lease Time',
      renderer: (value) =>
        typeof value === 'number' ? formatDuration(value) : value,
    },
    mac: { label: 'MAC Address' },
    src_port: { label: 'Src. Port' },
  },
  dns: {
    'dst_ip.address': { label: 'Dst. IP' },
    'src_ip.address': { label: 'Src. IP' },
    answer: { label: 'Answer', renderer: DnsAnswerRenderer },
    alexaDomainRank: { label: 'Alexa Domain Rank' },
    authoritative: { label: 'Authoritative', renderer: BooleanRenderer },
    dst_port: { label: 'Dst. Port' },
    'entropy.domain': { label: 'Domain Entropy' },
    qclass: { label: 'Query Class' },
    qtype: { label: 'Query Type' },
    query: { label: 'Query' },
    rcode: { label: 'Response Code' },
    src_port: { label: 'Src. Port' },
    undefined: { label: '# of Answers' },
  },
  endpoint: {
    'log_meta.device_event_id': { label: 'Event Id' },
    'log_meta.device_vendor': { label: 'Device' },
    'log_meta.device_product': { label: 'Product' },
    'log_meta.log_type': { label: 'Log Type' },
    action: { label: 'Action' },
    message: { label: 'Message' },
    username: { label: 'Username' },
  },
  file: {
    'bytes.total': { label: 'Size', renderer: (value) => formatBytes(value) },
    'src_ip.address': { label: 'Src. IP' },
    'dst_ip.address': { label: 'Dst. IP' },
    dst_port: { label: 'Dst. Port' },
    src_port: { label: 'Src. Port' },
    filename: { label: 'Filename' },
    hash: { label: 'Hash', renderer: HashRenderer },
    mime_type: { label: 'Mime Type' },
    source: { label: 'Source Protocol' },
    storedFilePath: { label: 'Download', renderer: StoredFilePathRenderer },
  },
  flow: {
    'dst_ip.address': { label: 'Dst. IP' },
    'src_ip.address': { label: 'Src. IP' },
    dst_port: { label: 'Dst. Port' },
    in_packets: { label: 'Inbound' },
    in_bytes: {
      label: 'Inbound Bytes',
      renderer: (value) => formatBytes(value),
    },
    out_bytes: {
      label: 'Outbound Bytes',
      renderer: (value) => formatBytes(value),
    },
    ip_proto: { label: 'IP Protocol', renderer: UppercaseRenderer },
    out_packets: { label: 'Outbound' },
    duration: {
      label: 'Duration',
      renderer: (value) =>
        typeof value === 'number' ? formatDuration(value) : value,
    },
    src_port: { label: 'Src. Port' },
  },
  ftp: {
    'dst_ip.address': { label: 'Dst. IP' },
    'src_ip.address': { label: 'Src. IP' },
    arg: { label: 'Argument' },
    cmdarg: {
      label: 'Current Command',
      renderer: (value) => (value ? `${value.cmd} ${value.arg}` : ''),
    },
    code: { label: 'Reply Code' },
    command: { label: 'Command' },
    cwd: { label: 'Current Working Directory' },
    data_channel: { label: 'Data Channel', renderer: DataChannelRenderer },
    dst_port: { label: 'Dst. Port' },
    file_size: { label: 'File Size' },
    mime_type: { label: 'MIME Type' },
    msg: { label: 'Reply Message' },
    passive: {
      label: 'Active/Passive',
      renderer: (value) => (value ? 'Passive' : 'Active'),
    },
    password: { label: 'Password', renderer: SanitizedRenderer },
    pending_commands: {
      label: 'Pending Commands',
      renderer: PendingCommandsRenderer,
    },
    src_port: { label: 'Src. Port' },
    user: { label: 'User' },
  },
  http: {
    'dst_ip.address': { label: 'Dst. IP' },
    'request.hostname': { label: 'Hostname' },
    'request.method': { label: 'HTTP Request Method' },
    'request.url': { label: 'URL' },
    'request.user_agent': { label: 'User Agent' },
    'response.status_code': { label: 'HTTP Response Status Code' },
    'response.response_body_len': {
      label: 'HTTP Response Body Length',
      renderer: (value) => formatBytes(value),
    },
    'src_ip.address': { label: 'Src. IP' },
    dst_port: { label: 'Dst. Port' },
    request: { label: 'HTTP Request', renderer: HttpRequestRenderer },
    response: { label: 'HTTP Response', renderer: HttpResponseRenderer },
    src_port: { label: 'Src. Port' },
  },
  irc: {
    'dst_ip.address': { label: 'Dst. IP' },
    'src_ip.address': { label: 'Src. IP' },
    additional_data: { label: 'Additional Data', renderer: SanitizedRenderer },
    command: { label: 'Command', renderer: SanitizedRenderer },
    dst_port: { label: 'Dst. Port' },
    nickname: { label: 'Nickname' },
    src_port: { label: 'Src. Port' },
    user: { label: 'User' },
    value: { label: 'Value', renderer: SanitizedRenderer },
  },
  kerberos: {
    'dst_ip.address': { label: 'Dst. IP' },
    'src_ip.address': { label: 'Src. IP' },
    cipher: { label: 'Cipher' },
    client_cert: {
      label: 'Client Cert Subject | fuid',
      renderer: CertSubjectRenderer,
    },
    client: { label: 'Client' },
    dst_port: { label: 'Dst. Port' },
    error_code: { label: 'Error Code' },
    error_msg: { label: 'Error Message' },
    forwardable: { label: 'Forwardable', renderer: BooleanRenderer },
    from: { label: 'From', renderer: DateRenderer },
    renewable: { label: 'Renewable', renderer: BooleanRenderer },
    request_type: { label: 'Request Type' },
    server_cert: {
      label: 'Server Cert Subject | fuid',
      renderer: CertSubjectRenderer,
    },
    service: { label: 'Service' },
    src_port: { label: 'Src. Port' },
    success: { label: 'Success' },
    till: { label: 'Till', renderer: DateRenderer },
  },
  proxy: {
    'dst_nat_ip.address': { label: 'Dst. NAT IP' },
    'endpoint_ip.address': { label: 'Endpoint IP' },
    'log_meta.device_event_id': { label: 'Event Id' },
    'log_meta.device_vendor': { label: 'Device' },
    'log_meta.device_product': { label: 'Product' },
    'log_meta.log_type': { label: 'Log Type' },
    'src_nat_ip.address': { label: 'Src. NAT IP' },
    'request.method': { label: 'Method' },
    'request.url': { label: 'URL' },
    'request.user_agent': { label: 'User Agent' },
    'response.response_body_len': {
      label: 'HTTP Response Body Length',
      renderer: (value) => formatBytes(value),
    },
    'response.status_code': { label: 'HTTP Response Status Code' },
    action: { label: 'Action' },
    endpoint_hostname: { label: 'Endpoint Hostname' },
    request: { label: 'HTTP Response', renderer: HttpResponseRenderer },
    transfer_size: { label: 'Transferred' },
  },
  notice: {
    'dst_ip.address': { label: 'Dst. IP' },
    'src_ip.address': { label: 'Src. IP' },
    detail: { label: 'Detail' },
    dst_port: { label: 'Dst. Port' },
    ip_proto: { label: 'Protocol', renderer: UppercaseRenderer },
    message: { label: 'Message' },
    metadata: { label: 'Metadata', renderer: MetadataRenderer },
    notice_type: { label: 'Notice Type' },
    source: { label: 'Source' },
    src_port: { label: 'Src. Port' },
  },
  ntlm: {
    'dst_ip.address': { label: 'Dst. IP' },
    'src_ip.address': { label: 'Src. IP' },
    domainname: { label: 'Domain' },
    dst_port: { label: 'Dst. Port' },
    hostname: { label: 'Hostname' },
    src_port: { label: 'Src. Port' },
    status: { label: 'Status' },
    success: { label: 'Successful' },
    username: { label: 'Username' },
  },
  radius: {
    'dst_ip.address': { label: 'Dst. IP' },
    'src_ip.address': { label: 'Src. IP' },
    'remote_ip.address': { label: 'Remote IP' },
    connect_info: { label: 'Connect Info' },
    dst_port: { label: 'Dst. Port' },
    mac: { label: 'MAC Address' },
    remote_ip: { label: 'Remote IP' },
    result: { label: 'Result' },
    src_port: { label: 'Src. Port' },
    username: { label: 'Username' },
  },
  rdp: {
    'client.build': { label: 'Client Build' },
    'client.name': { label: 'Client Product ID' },
    'dst_ip.address': { label: 'Dst. IP' },
    'encryption.level': { label: 'Encryption Level' },
    'encryption.method': { label: 'Encryption Method' },
    'src_ip.address': { label: 'Src. IP' },
    cert_count: { label: 'Cert Count' },
    cert_permanent: { label: 'Cert permanent', renderer: BooleanRenderer },
    cert_type: { label: 'Cert Type' },
    cookie: { label: 'Cookie' },
    desktop: {
      label: 'Desktop (Width x Height)',
      renderer: (value) => `${value.width} x ${value.height}`,
    },
    done: { label: 'Done', renderer: BooleanRenderer },
    dst_port: { label: 'Dst. Port' },
    keyboard_layout: { label: 'Keyboard Layout' },
    requested_color_depth: { label: 'Requested Color Depth' },
    result: { label: 'Result' },
    security_protocol: { label: 'Security Protocol' },
    src_port: { label: 'Src. Port' },
    ssl: { label: 'SSL' },
  },
  rfb: {
    'dst_ip.address': { label: 'Dst. IP' },
    'src_ip.address': { label: 'Src. IP' },
    authentication_method: { label: 'Authentication Method' },
    authentication_successful: { label: 'Authentication Successful' },
    client_major_version: { label: 'Client Major Version' },
    client_minor_version: { label: 'Client Minor Version' },
    desktop_name: { label: 'Desktop Name' },
    dst_port: { label: 'Dst. Port' },
    height: { label: 'Desktop Height' },
    server_major_version: { label: 'Server Major Version' },
    server_minor_version: { label: 'Server Minor Version' },
    shared_session: { label: 'Shared Session' },
    src_port: { label: 'Src. Port' },
    width: { label: 'Desktop Width' },
  },
  sip: {
    'dst_ip.address': { label: 'Dst. IP' },
    'request.from': { label: 'From' },
    'request.method': { label: 'Method' },
    'request.to': { label: 'To' },
    'src_ip.address': { label: 'Src. IP' },
    'headers.user_agent': { label: 'User Agent' },
    dst_port: { label: 'Dst. Port' },
    headers: { label: 'Headers', renderer: SipHeadersRenderer },
    request: { label: 'Request', renderer: SipRequestRenderer },
    src_port: { label: 'Src. Port' },
    status_code: { label: 'Status' },
  },
  smb: {
    'command.name': { label: 'Command Name' },
    'command.status': { label: 'Command Status' },
    'dst_ip.address': { label: 'Dst. IP' },
    'file.action': { label: 'Action' },
    'file.path': { label: 'Path' },
    'file.filename': { label: 'File Name' },
    'file.size': { label: 'File Size' },
    'file.timestamps.accessed': { label: 'Last Accessed' },
    'file.timestamps.changed': { label: 'Last Changed' },
    'file.timestamps.created': { label: 'Created' },
    'file.timestamps.modified': { label: 'Last Modified' },
    'file.uuid': { label: 'UUID' },
    'mapping.file_system': { label: 'File System' },
    'mapping.service': { label: 'Service' },
    'mapping.share_type': { label: 'Share Type' },
    'src_ip.address': { label: 'Src. IP' },
    dst_port: { label: 'Dst. Port' },
    path: { label: 'Path' },
    src_port: { label: 'Src. Port' },
  },
  smtp: {
    'dst_ip.address': { label: 'Dst. IP' },
    'headers.mailfrom': { label: 'Mail From' },
    'headers.rcptto': {
      label: 'Recipient To',
      renderer: (value) => (value && value.length ? value.join(', ') : value),
    },
    'src_ip.address': { label: 'Src. IP' },
    display_string: { label: 'Display String' },
    dst_port: { label: 'Dst. Port' },
    duration: { label: 'Duration' },
    get_bulk_requests: { label: 'Get Bulk Requests' },
    get_requests: { label: 'Get Requests' },
    get_responses: { label: 'Get Responses' },
    headers: { label: 'Headers', renderer: HeadersRenderer },
    is_webmail: { label: 'Webmail' },
    set_requests: { label: 'Set Requests' },
    src_port: { label: 'Src. Port' },
    tls: { label: 'TLS' },
    up_since: { label: 'Up Since' },
    user_agent: { label: 'User Agent' },
  },
  snmp: {
    'dst_ip.address': { label: 'Dst. IP' },
    'src_ip.address': { label: 'Src. IP' },
    community: { label: 'Community' },
    dst_port: { label: 'Dst. Port' },
    ip_proto: { label: 'IP Protocol', renderer: UppercaseRenderer },
    src_port: { label: 'Src. Port' },
    version: { label: 'SNMP Version' },
  },
  socks: {
    'bound.host': { label: 'Bound IP' },
    'bound.name': { label: 'Bound Host' },
    'dst_ip.address': { label: 'Dst. IP' },
    'request.host.address': { label: 'Req. IP' },
    'request.host.address.country_name': { label: 'Req. Country Name' },
    'request.name': { label: 'Remote Host' },
    'src_ip.address': { label: 'Src. IP' },
    bound_p: { label: 'Bound Port' },
    dst_port: { label: 'Dst. Port' },
    password: { label: 'Password' },
    request_p: { label: 'Req. Port' },
    src_port: { label: 'Src. Port' },
    status: { label: 'Status' },
    user: { label: 'Username' },
    version: { label: 'Version' },
  },
  ssh: {
    'dst_ip.address': { label: 'Dst. IP' },
    'src_ip.address': { label: 'Src. IP' },
    auth_success: { label: 'Authentication Successful' },
    cipher_algorithm: { label: 'Cipher Algorithm' },
    client: { label: 'Client' },
    compression_algorithm: { label: 'Compression Algorithm' },
    dst_port: { label: 'Dst. Port' },
    host_key_algorithm: { label: 'Host Key Algorithm' },
    ip_proto: { label: 'IP Protocol', renderer: UppercaseRenderer },
    key_exchange_algorithm: { label: 'Key Exchange Algorithm' },
    mac_algorithm: { label: 'MAC Algorithm' },
    server: { label: 'Server' },
    src_port: { label: 'Src. Port' },
    version: { label: 'SSH Version' },
  },
  threat: {
    'log_meta.device_event_id': { label: 'Event Id' },
    'log_meta.device_vendor': { label: 'Device' },
    'log_meta.device_product': { label: 'Product' },
    'log_meta.log_type': { label: 'Log Type' },
    action: { label: 'Action' },
    endpoint: { label: 'Endpoint' },
    event_name: { label: 'Event Name' },
    threat_value: { label: 'Threat Value' },
  },
  tls: {
    'dst_ip.address': { label: 'Dst. IP' },
    'src_ip.address': { label: 'Src. IP' },
    cipher: { label: 'Cipher Suite' },
    curve: { label: 'Elliptic Curve' },
    dst_port: { label: 'Dst. Port' },
    established: { label: 'Session Established?', renderer: BooleanRenderer },
    fingerprint: { label: 'Fingerprint' },
    ip_proto: { label: 'IP Protocol', renderer: UppercaseRenderer },
    issuer: { label: 'Issuer' },
    next_protocol: { label: 'Application Protocol' },
    resumed: { label: 'Session Resumed?', renderer: BooleanRenderer },
    server_name: { label: 'Server Name' },
    src_port: { label: 'Src. Port' },
    subject: { label: 'Subject' },
    version: { label: 'TLS Version' },
  },
  vuln: {
    'host_ip.address': { label: 'Host IP' },
    'log_meta.device_event_id': { label: 'Event Id' },
    'log_meta.device_vendor': { label: 'Device' },
    'log_meta.device_product': { label: 'Product' },
    'log_meta.log_type': { label: 'Log Type' },
    vuln_name: { label: 'Name' },
    vuln_severity_value: { label: 'Severity Value' },
    dst_fqdn: { label: 'Dst. FQDN' },
    endpoint: { label: 'Endpoint' },
  },
  windows: {
    'log_meta.device_vendor': { label: 'Device' },
    'log_meta.device_product': { label: 'Product' },
    'endpoint_ip.address': { label: 'Endpoint IP' },
    channel: { label: 'Channel' },
    endpoint_hostname: { label: 'Endpoint Hostname' },
    endpoint_username: { label: 'Endpoint Username' },
    levelString: { label: 'Level String' },
    event_id: { label: 'Event ID' },
  },
  email: {
    'dst_ip.address': { label: 'Dst. IP' },
    'src_ip.address': { label: 'Src. IP' },
    dst_fqdn: { label: 'Dst. FQDN' },
    recipient: { label: 'Recipient' },
    sender: { label: 'Sender' },
    src_fqdn: { label: 'Src. FQDN' },
    subject: { label: 'Subject' },
  },
};

export const getRecordRenderMap = memoize(() => {
  const result: RecordRenderMapType = {};
  Object.entries(recordRenderMap).forEach(([key, value]) => {
    result[key] = { ...value, ...commonRecordMeta };
  });

  return result;
});

export interface RecordColumnType {
  primaryLabel: string;
  primaryName?: string;
  secondaryLabel?: string;
  secondaryName?: string;
}

const sysLogCol = {
  primary: 'log_meta.device_vendor',
  secondary: 'log_meta.device_product',
};
const networkSrcCol = { primary: 'src_ip.address', secondary: 'src_port' };
const networkDstCol = { primary: 'dst_ip.address', secondary: 'dst_port' };

// The order of these properties is significant.
export const recordColumnMap: RecordColumnMapType = {
  auth: [sysLogCol, { renderer: priRenderer.auth }],
  canary: [
    {
      primary: 'hatchtime',
      renderer: (value) =>
        typeof value === 'number' ? ParsedDateRenderer(value) : value,
      secondaryRenderer: (_value, { hatchtime }) =>
        // Do not render the label.
        hatchtime && ParsedTimeRenderer(hatchtime),
    },
    { primary: 'debug' },
  ],
  dcerpc: [networkSrcCol, networkDstCol, { renderer: priRenderer.dcerpc }],
  dhcp: [networkSrcCol, networkDstCol, { renderer: priRenderer.dhcp }],
  dns: [
    networkSrcCol,
    networkDstCol,
    { renderer: priRenderer.dns, secondaryRenderer: secRenderer.dns },
  ],
  endpoint: [sysLogCol, { renderer: priRenderer.endpoint }],
  file: [
    networkSrcCol,
    networkDstCol,
    { renderer: priRenderer.file, secondaryRenderer: secRenderer.file },
  ],
  flow: [
    networkSrcCol,
    networkDstCol,
    { renderer: priRenderer.flow, secondaryRenderer: secRenderer.flow },
  ],
  ftp: [networkSrcCol, networkDstCol, { renderer: priRenderer.ftp }],
  http: [
    networkSrcCol,
    networkDstCol,
    { renderer: priRenderer.http, secondaryRenderer: secRenderer.http },
  ],
  irc: [
    networkSrcCol,
    networkDstCol,
    { renderer: priRenderer.irc, secondaryRenderer: secRenderer.irc },
  ],
  kerberos: [networkSrcCol, networkDstCol, { renderer: priRenderer.kerberos }],
  proxy: [
    sysLogCol,
    { renderer: priRenderer.proxy, secondaryRenderer: secRenderer.proxy },
  ],
  notice: [
    networkSrcCol,
    networkDstCol,
    { renderer: priRenderer.notice, secondaryRenderer: secRenderer.notice },
  ],
  ntlm: [
    networkSrcCol,
    networkDstCol,
    { renderer: priRenderer.ntlm, secondaryRenderer: secRenderer.ntlm },
  ],
  radius: [
    networkSrcCol,
    networkDstCol,
    { renderer: priRenderer.radius, secondaryRenderer: secRenderer.radius },
  ],
  rdp: [
    networkSrcCol,
    networkDstCol,
    { renderer: priRenderer.rdp, secondaryRenderer: secRenderer.rdp },
  ],
  rfb: [
    networkSrcCol,
    networkDstCol,
    { renderer: priRenderer.rfb, secondaryRenderer: secRenderer.rfb },
  ],
  sip: [
    networkSrcCol,
    networkDstCol,
    { renderer: priRenderer.sip, secondaryRenderer: secRenderer.sip },
  ],
  smb: [
    networkSrcCol,
    networkDstCol,
    { renderer: priRenderer.smb, secondaryRenderer: secRenderer.smb },
  ],
  smtp: [
    networkSrcCol,
    networkDstCol,
    { renderer: priRenderer.smtp, secondaryRenderer: secRenderer.smtp },
  ],
  snmp: [
    networkSrcCol,
    networkDstCol,
    { renderer: priRenderer.snmp, secondaryRenderer: secRenderer.snmp },
  ],
  socks: [networkSrcCol, networkDstCol, { renderer: priRenderer.socks }],
  ssh: [
    networkSrcCol,
    networkDstCol,
    { renderer: priRenderer.ssh, secondaryRenderer: secRenderer.ssh },
  ],
  threat: [sysLogCol, { renderer: priRenderer.threat }],
  tls: [
    networkSrcCol,
    networkDstCol,
    { renderer: priRenderer.tls, secondaryRenderer: secRenderer.tls },
  ],
  vuln: [sysLogCol, { renderer: priRenderer.vuln }],
  windows: [
    sysLogCol,
    { renderer: priRenderer.windows, secondaryRenderer: secRenderer.windows },
  ],
  email: [networkSrcCol, networkDstCol, { renderer: priRenderer.email }],
};

const recordTypes = {
  network: [
    'dcerpc',
    'dhcp',
    'dns',
    'email',
    'file',
    'flow',
    'ftp',
    'http',
    'irc',
    'kerberos',
    'notice',
    'ntlm',
    'radius',
    'rdp',
    'rfb',
    'sip',
    'smb',
    'smtp',
    'snmp',
    'socks',
    'ssh',
    'tls',
  ],
  syslog: ['auth', 'endpoint', 'proxy', 'threat', 'vuln', 'windows'],
};

export const getRecordCategory = (type: string) =>
  Object.keys(recordTypes).find((key) => recordTypes[key].includes(type));
